

import React, { useState, useRef, useEffect } from "react";
import { Card, Box, TextField, Button } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useMaterialUIController } from "context";
import { WebRTCAdaptor } from "@antmedia/webrtc_adaptor";

export default function PTTButton() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [roomId, setRoomId] = useState("999999");
  const [webUrl, setWebUrl] = useState("wss://gujv3.coraltele.com:5443/LiveApp/websocket");
  const [isConnected, setIsConnected] = useState(false);
  const [isPushing, setIsPushing] = useState(false);

  const webRTCAdaptorRef = useRef(null);
  const streamIdRef = useRef(`stream${Date.now()}`);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (webRTCAdaptorRef.current) {
        try {
          webRTCAdaptorRef.current.stop(streamIdRef.current);
          webRTCAdaptorRef.current.closeWebSocket();
        } catch (e) {
          console.warn("Cleanup error:", e);
        }
      }
    };
  }, []);

  const handleConnect = () => {
    if (!roomId || !webUrl) {
      alert("Please enter Room ID and WebSocket URL");
      return;
    }

    try {
      console.log("📡 [PTT] Connecting to:", webUrl, "Room:", roomId);

      const adaptor = new WebRTCAdaptor({
        websocket_url: webUrl,
        callback: (info, obj) => {
          console.log("📡 [PTT] Callback:", info, obj);

          if (info === "initialized") {
            console.log("✅ [PTT] Initialized");
            adaptor.publish(streamIdRef.current, null, null, null, null, roomId, null, null);
          } else if (info === "newTrackAvailable") {
            playTrack(obj);
          } else if (info === "publish_started") {
            console.log("✅ [PTT] Publish started");
            adaptor.turnOffLocalCamera(streamIdRef.current);
            adaptor.muteLocalMic();
            adaptor.play(roomId);
            setIsConnected(true);
          } else if (info === "publish_finished") {
            console.log("🔴 [PTT] Publish finished");
            setIsConnected(false);
          }
        },
        callbackError: (error, message) => {
          console.error("❌ [PTT] Error:", error, message);
          setIsConnected(false);
        },
      });

      webRTCAdaptorRef.current = adaptor;
    } catch (error) {
      console.error("❌ [PTT] Failed to initialize:", error);
      alert("Failed to connect. Please check console for errors.");
    }
  };

  const handleDisconnect = () => {
    if (webRTCAdaptorRef.current) {
      try {
        webRTCAdaptorRef.current.stop(streamIdRef.current);
        webRTCAdaptorRef.current.closeWebSocket();
        setIsConnected(false);
        console.log("🔴 [PTT] Disconnected");
      } catch (e) {
        console.warn("⚠️ [PTT] Disconnect error:", e);
      }
    }
  };

  const handlePushDown = () => {
    if (!isConnected || !webRTCAdaptorRef.current) return;

    console.log("🎤 [PTT] Button pressed - unmuting");
    webRTCAdaptorRef.current.turnOnLocalCamera(streamIdRef.current);
    webRTCAdaptorRef.current.unmuteLocalMic(streamIdRef.current);
    setIsPushing(true);
  };

  const handlePushUp = () => {
    if (!isConnected || !webRTCAdaptorRef.current) return;

    console.log("🔇 [PTT] Button released - muting");
    webRTCAdaptorRef.current.turnOffLocalCamera(streamIdRef.current);
    webRTCAdaptorRef.current.muteLocalMic(streamIdRef.current);
    setIsPushing(false);
  };

  const playTrack = (obj) => {
    const incomingTrackId = obj.trackId.substring("ARDAMSx".length);
    
    if (incomingTrackId === roomId || incomingTrackId === streamIdRef.current) {
      return;
    }

    console.log("🎵 [PTT] New track received:", incomingTrackId);

    // Check if player already exists
    let video = document.getElementById(`remoteVideo${incomingTrackId}`);
    
    if (!video) {
      const player = document.createElement("div");
      player.id = `player${incomingTrackId}`;
    //   player.style.display = ""; // Hide video element (audio only)
      player.innerHTML = `<video id="remoteVideo${incomingTrackId}" autoplay playsinline></video>`;
      document.getElementById("ptt-players").appendChild(player);
      video = document.getElementById(`remoteVideo${incomingTrackId}`);
    }

    if (!video.srcObject) {
      video.srcObject = new MediaStream();
    }
    video.srcObject.addTrack(obj.track);
  };

  return (
    <MDBox p={2}>
      <Card
        sx={{
          p: 3,
          background: darkMode
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
            : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)",
          boxShadow: darkMode
            ? "0 20px 60px rgba(0, 0, 0, 0.6)"
            : "0 20px 60px rgba(0, 0, 0, 0.1)",
          borderRadius: 4,
        }}
      >
        <MDTypography variant="h4" mb={3} textAlign="center" color={darkMode ? "white" : "dark"}>
          Push-to-Talk (PTT)
        </MDTypography>

        {/* Configuration Inputs */}
        <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Room ID"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            disabled={isConnected}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                color: darkMode ? "#fff" : "#000",
                "& fieldset": {
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.23)",
                },
              },
              "& .MuiInputLabel-root": {
                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
              },
            }}
          />

          <TextField
            label="WebSocket URL"
            value={webUrl}
            onChange={(e) => setWebUrl(e.target.value)}
            disabled={isConnected}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                color: darkMode ? "#fff" : "#000",
                "& fieldset": {
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.23)",
                },
              },
              "& .MuiInputLabel-root": {
                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
              },
            }}
          />

          {!isConnected ? (
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleConnect}
              fullWidth
              size="large"
            >
              Connect
            </MDButton>
          ) : (
            <MDButton
              variant="gradient"
              color="error"
              onClick={handleDisconnect}
              fullWidth
              size="large"
            >
              Disconnect
            </MDButton>
          )}
        </Box>

        {/* PTT Button */}
        {isConnected && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              onMouseDown={handlePushDown}
              onMouseUp={handlePushUp}
              onTouchStart={handlePushDown}
              onTouchEnd={handlePushUp}
              variant="contained"
              sx={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: !isPushing
                  ? "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)"
                  : "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
                boxShadow: !isPushing
                  ? "0 0 40px rgba(244, 67, 54, 0.6)"
                  : "0 8px 30px rgba(76, 175, 80, 0.4)",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: !isPushing
                    ? "0 0 50px rgba(244, 67, 54, 0.8)"
                    : "0 12px 40px rgba(76, 175, 80, 0.6)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                {isPushing ? (
                  <MicIcon sx={{ fontSize: 80, color: "#fff" }} />
                ) : (
                  <MicOffIcon sx={{ fontSize: 80, color: "#fff" }} />
                )}
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  {isPushing ? "TALKING" : "PUSH"}
                </MDTypography>
              </Box>
            </Button>
          </Box>
        )}

        {/* Status Indicator */}
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <MDTypography
            variant="body2"
            sx={{
              color: isConnected ? "#4caf50" : "#999",
              fontWeight: 600,
            }}
          >
            {isConnected ? "🟢 Connected" : "⚫ Disconnected"}
          </MDTypography>
        </Box>

        {/* Hidden players container */}
        <div id="ptt-players" style={{ display: "none" }}></div>
      </Card>
    </MDBox>
  );
}
